import cloneDeep from 'lodash/cloneDeep';
import {
  Box,
  FormControl,
  ListItemIcon,
  MenuItem,
  Popover,
  SelectChangeEvent,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { uniqueId } from '../../lib/uniqueId';
import { ActionObjectTypesAutocomplete } from './ActionObjectTypesAutocomplete';
import { ChildRelationshipSelector } from './ChildRelationshipSelector';
import { AutomationActionType } from '../../types/enums/AutomationActionType';
import SweepSelect from '../common/SweepSelect';
import { AutomationNotifyEmail } from './AutomationNotifyEmail';
import { AutomationActionRows } from './AutomationActionRows';
import { SlackNotificationAction } from './slack/SlackNotificationAction';
import { Button, Typography, colors } from '@sweep-io/sweep-design';
import { SelectorValueTypes } from '../../types/enums/SelectorValueTypes';
import { CustomHeader } from '../common/rule-builder/CustomHeader';
import { SweepFieldsRuleBuilder } from '../common/sweep-condition-rule-builder/SweepConditionRuleBuilder';
import { useRefForMultipleRuleBuilders } from '../common/sweep-condition-rule-builder/MultipleRuleBuildersContext';
import { ConvertLeadActionNew } from './ConvertLeadActionNew';
import { ConvertLeadConvertedStatus } from './ConvertLeadConvertedStatus';
import { ContactRoleSelector, lookupFieldsTypes } from './ContactRoleSelector';
import { useSelector } from 'react-redux';
import {
  SlackConnectionStatus,
  TeamsConnectionStatus,
  selectSlackIntegrationStatus,
  selectTeamsIntegrationStatus,
} from '../../reducers/integrationsReducer';
import { SlackDealRoomAction } from './slack/SlackDealRoomAction';
import { useRunOnce } from '../common/useRunOnce';
import { useIntegrations } from '../../hooks/useIntegrations';
import { SlackAddMemberAction } from './slack/SlackAddMemberAction';
import AssignmentRulesEditor from '../routing/AssignmentRulesEditor';
import { SlackNotConnected } from './slack/SlackNotConnected';
import {
  AutomationActionDescriptionByType,
  DividerLighter,
  getOperatorsWithoutRecordsComparison,
} from './helper';
import slackSampleImg from './images/slackSample.png';
import teamsSampleImg from './images/teamsSample.png';
import { SendReportToSlackAction } from './slack/SendReportToSlackAction';
import { useFeatureToggle } from '../../feature-flags/useFeatureToggle';
import { NestedFieldsSelector } from '../common/fieldsSelectors/NestedFieldsSelector';
import { useSweepFieldsInfo } from '../../sweep-fields/useSweepFieldsInfo';
import { VerifyUrlActionWrapper } from './VerifyUrlActionWrapper';
import { SlackRenameDealroomAction } from './slack/SlackRenameDealroomAction';
import { SlackArchiveDealroomAction } from './slack/SlackArchiveDealroomAction';
import ConditionalActionsEditor from './ConditionalActionsEditor';
import {
  AddUsers,
  Archive,
  CheckCircle,
  Dealroom,
  Edit,
  GitBranch,
  Integrations,
  Mail,
  PlusCircle,
  Refresh,
  SlackLogo,
  UserSwitch,
  MicrosoftTeams,
  PlaybookAlert,
  Copy,
} from '@sweep-io/sweep-design/dist/icons';
import { WebhookActionWrapper } from './WebhookActionWrapper';
import { WebhookActionSelector } from './WebhookActionSelector';
import { useCrmOrgsApiFacade } from '../../apis/facades/useCrmOrgsApiFacade';
import { telemetry } from '../../telemetry';
import { RunApexActionWrapper } from './RunApexActionWrapper';
import { TeamsMessageAction } from './TeamsMessageAction';
import { PushNotificationAction } from './PushNotificationAction';
import { TeamsAddMemberAction } from './teams/TeamsAddMemberAction';
import { DealroomType, DealroomTypeSelector } from './teams/DealroomTypeSelector';
import { TeamsDealRoomAction } from './teams/TeamsDealRoomAction';
import { TeamsRenameDealRoomAction } from './teams/TeamsRenameDealRoomAction';
import { TeamsArchiveDealroomAction } from './teams/TeamsArchiveDealroomAction';
import { SfdcDisplayType } from '../../types/fields';
import { SweepCondition } from '../../types/SweepCondition';

interface AutomationActionProps {
  action?: DeepPartial<AutomationAction>;
  onChange: (action: Partial<AutomationAction>) => any;
  objectName: string;
  crmOrgId: string;
  readonly?: boolean;
  isActionEnabled: boolean;
  objectTypes?: ObjectTypeName[];
  isPlaybookAlerts?: boolean;
  excludeValueType?: (type: SelectorValueTypes) => boolean;
  isHideActionSelector?: boolean;
  isNestedVerifiedUrl?: string;
  actionsToExclude?: AutomationActionType[];
  isShowWebhookOption?: boolean;
}

type ThenTypesProps = {
  value: AutomationActionType;
  label: string;
  icon: JSX.Element;
  groupIndex: number;
};

const OPPORTUNITY = 'Opportunity';
const CONTACT = 'Contact';
const OPPORTUNITY_CONTACT_ROLE = 'OpportunityContactRole';
const OPPORTUNITY_LINE_ITEM = 'OpportunityLineItem';

const slackDealRoomActionTypes = [
  AutomationActionType.SlackDealRoom,
  AutomationActionType.SlackAddMember,
  AutomationActionType.SlackRenameDealroom,
  AutomationActionType.SlackArchiveDealroom,
];

const teamsDealRoomActionTypes = [
  AutomationActionType.TeamsDealRoom,
  AutomationActionType.TeamsAddMember,
  AutomationActionType.TeamsRenameDealroom,
  AutomationActionType.TeamsArchiveDealroom,
];

export const AutomationAction = ({
  action,
  onChange,
  objectName,
  crmOrgId,
  readonly,
  isActionEnabled,
  objectTypes,
  isPlaybookAlerts,
  excludeValueType,
  isHideActionSelector,
  isNestedVerifiedUrl,
  actionsToExclude,
  isShowWebhookOption,
}: AutomationActionProps) => {
  const [recordTypesList, setRecordTypes] = useState<RecordTypeStruct[]>();
  const slackStatus = useSelector(selectSlackIntegrationStatus);
  const teamsStatus = useSelector(selectTeamsIntegrationStatus);
  const integrations = useIntegrations();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isSampleOpen = Boolean(anchorEl);
  const { postToMsTeamsAction, lgDemo, msTeamsCreateAndAddAction } = useFeatureToggle();
  const { getEnrichedNestedPath } = useSweepFieldsInfo();
  const isShowDealRoomType = msTeamsCreateAndAddAction;

  const setFirstDealRoomType = useMemo(() => {
    if (!msTeamsCreateAndAddAction) {
      return DealroomType.SLACK;
    }

    if (
      action?.actionType &&
      slackDealRoomActionTypes.includes(action.actionType as AutomationActionType)
    ) {
      return DealroomType.SLACK;
    }

    if (
      action?.actionType &&
      teamsDealRoomActionTypes.includes(action.actionType as AutomationActionType)
    ) {
      return DealroomType.TEAMS;
    }

    return undefined;
  }, [msTeamsCreateAndAddAction, action?.actionType]);

  const [dealRoomType, setDealRoomType] = useState<DealroomType | undefined>(setFirstDealRoomType);

  const isActionDealroomType = (actionType: AutomationActionType) => {
    return (
      slackDealRoomActionTypes.includes(actionType) ||
      (teamsDealRoomActionTypes.includes(actionType) && msTeamsCreateAndAddAction)
    );
  };

  useRunOnce(async () => {
    await Promise.all([integrations.getSlackStatus(), integrations.getTeamsStatus()]);
  });

  const _action: DeepPartial<AutomationAction> = useMemo(
    () =>
      action || {
        _id: uniqueId(),
        actionParams: undefined,
        actionType: undefined,
      },
    [action],
  );

  let ThenTypes: ThenTypesProps[] = useMemo(() => {
    let ThenTypesInner = [
      {
        value: AutomationActionType.CreateRecord,
        label: AutomationActionDescriptionByType[AutomationActionType.CreateRecord],
        icon: <PlusCircle size="medium" />,
        groupIndex: 0,
      },
      {
        value: AutomationActionType.UpdateRecords,
        label: AutomationActionDescriptionByType[AutomationActionType.UpdateRecords],
        icon: <Refresh size="medium" />,
        groupIndex: 0,
      },
      {
        value: AutomationActionType.SendEmail,
        label: AutomationActionDescriptionByType[AutomationActionType.SendEmail],
        icon: <Mail size="medium" />,
        groupIndex: 1,
      },
      {
        value: AutomationActionType.PrioritizedActions,
        label: AutomationActionDescriptionByType[AutomationActionType.PrioritizedActions],
        icon: <GitBranch size="medium" />,
        groupIndex: 2,
      },
      {
        value: AutomationActionType.Webhook,
        label: AutomationActionDescriptionByType[AutomationActionType.Webhook],
        icon: <Integrations size="medium" />,
        groupIndex: 2,
      },
      {
        value: AutomationActionType.SlackDealRoom,
        label: AutomationActionDescriptionByType[AutomationActionType.SlackDealRoom],
        icon: <Dealroom size="medium" />,
        groupIndex: 3,
      },
      {
        value: AutomationActionType.SlackAddMember,
        label: AutomationActionDescriptionByType[AutomationActionType.SlackAddMember],
        icon: <AddUsers size="medium" />,
        groupIndex: 3,
      },
      {
        value: AutomationActionType.SlackRenameDealroom,
        label: AutomationActionDescriptionByType[AutomationActionType.SlackRenameDealroom],
        icon: <Edit size="medium" />,
        groupIndex: 3,
      },
      {
        value: AutomationActionType.SlackArchiveDealroom,
        label: AutomationActionDescriptionByType[AutomationActionType.SlackArchiveDealroom],
        icon: <Archive size="medium" />,
        groupIndex: 3,
      },
    ];

    if ('Lead' === objectName) {
      ThenTypesInner.push({
        value: AutomationActionType.ConvertLead,
        label: AutomationActionDescriptionByType[AutomationActionType.ConvertLead],
        icon: <UserSwitch size="medium" />,
        groupIndex: 2,
      });
    }

    if (lgDemo) {
      // TODO: REMOVE with ft lgDemo
      ThenTypesInner.push({
        value: AutomationActionType.CloneRecords,
        label: AutomationActionDescriptionByType[AutomationActionType.CloneRecords],
        icon: <Copy size="medium" />,
        groupIndex: 0,
      });
    }

    if (!isNestedVerifiedUrl) {
      ThenTypesInner.push({
        value: AutomationActionType.VerifyURL,
        label: AutomationActionDescriptionByType[AutomationActionType.VerifyURL],
        icon: <CheckCircle size="medium" />,
        groupIndex: 2,
      });
    }
    if (
      slackStatus === SlackConnectionStatus.Connected ||
      teamsStatus !== TeamsConnectionStatus.Connected
    ) {
      ThenTypesInner.push({
        value: AutomationActionType.Slack,
        label: AutomationActionDescriptionByType[AutomationActionType.Slack],
        icon: <SlackLogo size="medium" />,
        groupIndex: 1,
      });
    }

    if (isPlaybookAlerts) {
      ThenTypesInner = [
        {
          value: AutomationActionType.SendEmail,
          label: AutomationActionDescriptionByType[AutomationActionType.SendEmail],
          icon: <Mail size="medium" />,
          groupIndex: 1,
        },
        {
          value: AutomationActionType.Slack,
          label: AutomationActionDescriptionByType[AutomationActionType.Slack],
          icon: <SlackLogo size="medium" />,
          groupIndex: 1,
        },
      ];
    }

    if (teamsStatus === TeamsConnectionStatus.Connected && postToMsTeamsAction) {
      ThenTypesInner.push({
        value: AutomationActionType.SendTeamsMessage,
        label: AutomationActionDescriptionByType[AutomationActionType.SendTeamsMessage],
        icon: <MicrosoftTeams size="medium" />,
        groupIndex: 1,
      });
    }
    if (lgDemo) {
      // TODO: REMOVE with ft lgDemo
      ThenTypesInner.push({
        value: AutomationActionType.PushNotification,
        label: AutomationActionDescriptionByType[AutomationActionType.PushNotification],
        icon: <PlaybookAlert size="medium" />,
        groupIndex: 1,
      });
    }

    return ThenTypesInner;
  }, [
    isNestedVerifiedUrl,
    isPlaybookAlerts,
    lgDemo,
    objectName,
    postToMsTeamsAction,
    slackStatus,
    teamsStatus,
  ]);

  if (actionsToExclude) {
    actionsToExclude.map((excludeEl) => {
      ThenTypes = ThenTypes.filter((el) => el.value !== excludeEl);
    });
  }

  const getActionRowObjectType = () => {
    return action?.actionType === AutomationActionType.CreateRecord || // TODO: REMOVE 'CloneRecords' with ft lgDemo
      action?.actionType === AutomationActionType.CloneRecords
      ? (_action as UpdateOrCreateAutomationAction).actionParams?.objectTypeName?.objectType
      : (_action as UpdateOrCreateAutomationAction).actionParams?.thisRecord
        ? objectName
        : (_action as UpdateOrCreateAutomationAction).actionParams?.childRelationshipFieldName
            ?.objectType;
  };

  const hasRecordTypes = () => {
    return (
      (_action as UpdateOrCreateAutomationAction).actionParams?.objectTypeName?.recordType ||
      (recordTypesList && recordTypesList.length > 1)
    );
  };

  const { getObjectFunnelsAndRecordTypes } = useCrmOrgsApiFacade();
  const setRecordTypePerObject = useCallback(
    async (action: any) => {
      const objectName =
        (action as UpdateOrCreateAutomationAction)?.actionParams?.objectTypeName?.objectType ?? '';
      try {
        const result = await getObjectFunnelsAndRecordTypes({
          crmOrgId,
          objectName,
        });
        setRecordTypes(result?.recordTypes || []);
      } catch (error) {
        telemetry.captureError(error);
        setRecordTypes([]);
      }
    },
    [crmOrgId, getObjectFunnelsAndRecordTypes],
  );

  const criteria = useMemo(
    () => cloneDeep((_action as UpdateOrCreateAutomationAction).actionParams?.criteria),
    [_action],
  );

  const sweepCondition: SweepCondition | undefined = useMemo(() => {
    const criteria = cloneDeep((_action as UpdateOrCreateAutomationAction).actionParams?.criteria);
    if (!criteria) {
      return undefined;
    }
    return {
      criteria: criteria.criteria,
      criteriaLogic: criteria.criteriaLogic,
    };
  }, [_action]);

  const ruleBuilderRef = useRefForMultipleRuleBuilders();

  const getThenTypesList = useMemo(() => {
    ThenTypes.sort((a, b) => a.groupIndex - b.groupIndex);
    let currentGroupIndex = ThenTypes?.[0]?.groupIndex ?? 0;
    const elemList = ThenTypes.map((type) => {
      const item = (
        <MenuItem key={type.value} value={type.value}>
          <ListItemIcon>{type.icon}</ListItemIcon>
          {type.label}
        </MenuItem>
      );
      if (type.groupIndex > currentGroupIndex) {
        currentGroupIndex = type.groupIndex;
        return [<DividerLighter key={`divider${currentGroupIndex}`} />, item];
      } else {
        return item;
      }
    });

    return elemList;
  }, [ThenTypes]);

  const _onChangeActionRowFields = useCallback(
    (fields: AutomationActionField[]) => {
      onChange({
        ...(action as DeepPartial<UpdateOrCreateAutomationAction>),
        actionParams: {
          ...(action as UpdateOrCreateAutomationAction)?.actionParams,
          _fields: fields,
        },
      });
    },
    [action, onChange],
  );

  //TODO: add rename and archive dealroom when ready in the backend
  const getDealRoomActionType = (type: DealroomType) => {
    if (type === DealroomType.SLACK) {
      switch (action?.actionType) {
        case AutomationActionType.TeamsAddMember:
          return AutomationActionType.SlackAddMember;
        case AutomationActionType.TeamsDealRoom:
          return AutomationActionType.SlackDealRoom;
        case AutomationActionType.TeamsRenameDealroom:
          return AutomationActionType.SlackRenameDealroom;
        case AutomationActionType.TeamsArchiveDealroom:
          return AutomationActionType.SlackArchiveDealroom;
        default:
          return action?.actionType;
      }
    } else {
      switch (action?.actionType) {
        case AutomationActionType.SlackAddMember:
          return AutomationActionType.TeamsAddMember;
        case AutomationActionType.SlackDealRoom:
          return AutomationActionType.TeamsDealRoom;
        case AutomationActionType.SlackRenameDealroom:
          return AutomationActionType.TeamsRenameDealroom;
        case AutomationActionType.SlackArchiveDealroom:
          return AutomationActionType.TeamsArchiveDealroom;
        default:
          return action?.actionType;
      }
    }
  };

  const actionTypeForMenuDisplay = useMemo(() => {
    if (!action?.actionType) return '';
    switch (action.actionType) {
      case AutomationActionType.TeamsAddMember:
        return AutomationActionType.SlackAddMember;
      case AutomationActionType.TeamsDealRoom:
        return AutomationActionType.SlackDealRoom;
      case AutomationActionType.TeamsRenameDealroom:
        return AutomationActionType.SlackRenameDealroom;
      case AutomationActionType.TeamsArchiveDealroom:
        return AutomationActionType.SlackArchiveDealroom;
      default:
        return action.actionType;
    }
  }, [action?.actionType]);

  return (
    <>
      <Box
        className="autoThenSelect"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          rowGap: '10px',
        }}
      >
        {!isHideActionSelector && action?.actionType !== AutomationActionType.AssignmentRules && (
          <SweepSelect
            menuMaxHeight="520px"
            FormControlProps={{
              sx: { minWidth: '120px', marginRight: '8px' },
            }}
            SelectProps={{
              disabled: !isActionEnabled || readonly,
              placeholder: isPlaybookAlerts ? 'Choose how to notify' : 'Do This',
              value: actionTypeForMenuDisplay ?? '',
              MenuProps: {
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
                sx: {
                  '.MuiMenu-paper .MuiMenuItem-root .MuiListItemIcon-root': {
                    minWidth: 2,
                    mr: 1,
                  },
                },
              },
              onChange: (event: SelectChangeEvent<unknown>) => {
                const val = event.target.value as any;
                setDealRoomType(undefined);
                onChange({
                  ..._action,
                  actionType: val,
                  actionParams: undefined,
                });
              },
              renderValue: (val: any) => {
                const output = ThenTypes.filter((d) => d.value === val);
                if (output.length > 0) {
                  return output[0].label ?? '';
                }
              },
            }}
          >
            {getThenTypesList}
          </SweepSelect>
        )}
        {action?.actionType === AutomationActionType.Webhook && (
          <WebhookActionSelector
            readonly={readonly}
            action={_action as WebhookAutomationAction}
            onChange={onChange}
          />
        )}

        {isShowDealRoomType && isActionDealroomType(action?.actionType as AutomationActionType) && (
          <DealroomTypeSelector
            type={dealRoomType}
            onChange={(type) => {
              setDealRoomType(type);
              const _actionType = getDealRoomActionType(type);
              onChange({
                ..._action,
                actionType: _actionType as AutomationActionType,
                actionParams: undefined,
              });
            }}
          />
        )}

        {(action?.actionType === AutomationActionType.CreateRecord || // TODO: REMOVE 'CloneRecords' with ft lgDemo
          action?.actionType === AutomationActionType.CloneRecords) && (
          <>
            <Box>
              <FormControl sx={{ minWidth: '160px', marginRight: '8px', width: '100%' }}>
                <ActionObjectTypesAutocomplete
                  _action={_action as UpdateOrCreateAutomationAction}
                  crmOrgId={crmOrgId}
                  disabled={readonly}
                  onChange={(action) => {
                    onChange(action as UpdateOrCreateAutomationAction);
                    setRecordTypePerObject(action);
                  }}
                  objectTypeFilters={{
                    createable: true,
                  }}
                />
              </FormControl>
            </Box>
          </>
        )}
        {(action?.actionType === AutomationActionType.CreateRecord || // TODO: REMOVE 'CloneRecords' with ft lgDemo
          action?.actionType === AutomationActionType.CloneRecords) &&
          hasRecordTypes() && (
            <>
              <Box>
                <SweepSelect
                  FormControlProps={{
                    sx: { minWidth: '140px', marginLeft: '8px' },
                  }}
                  SelectProps={{
                    disabled: readonly,
                    placeholder: 'Choose Record Type',
                    value:
                      (_action as UpdateOrCreateAutomationAction).actionParams?.objectTypeName
                        ?.recordType?.name ?? '',
                    renderValue: (val: any) => {
                      if (!recordTypesList) {
                        setRecordTypePerObject(_action);
                      }
                      const recordType = recordTypesList?.find((type) => type?.name === val);
                      return recordType?.label ?? val ?? '';
                    },
                    onChange: (event: SelectChangeEvent<unknown>) => {
                      const val = event.target.value;
                      const getRecordTypeByName = recordTypesList?.find(
                        (type) => type?.name === val,
                      );
                      const newObjectTypeName = {
                        ...action.actionParams?.objectTypeName,
                        recordType: getRecordTypeByName,
                      };
                      onChange({
                        ...(_action as UpdateOrCreateAutomationAction),
                        actionParams: {
                          ...(_action as UpdateOrCreateAutomationAction).actionParams,
                          objectTypeName: newObjectTypeName as ObjectTypeName,
                        },
                      });
                    },
                  }}
                >
                  {recordTypesList?.map((type) => (
                    <MenuItem key={type.name} value={type.name}>
                      {type.label}
                    </MenuItem>
                  ))}
                </SweepSelect>
              </Box>
            </>
          )}
        {action?.actionType === AutomationActionType.UpdateRecords && (
          <>
            <Box>
              <SweepSelect
                FormControlProps={{
                  sx: { minWidth: '140px', marginRight: '8px' },
                }}
                SelectProps={{
                  disabled: readonly,
                  placeholder: 'Choose a Record',
                  value: (_action as UpdateOrCreateAutomationAction).actionParams?.thisRecord ?? '',
                  onChange: (event: SelectChangeEvent<unknown>) => {
                    const val = event.target.value;
                    onChange({
                      ...(_action as UpdateOrCreateAutomationAction),
                      actionParams: {
                        ...(_action as UpdateOrCreateAutomationAction).actionParams,
                        thisRecord: val as boolean,
                        childRelationshipFieldName: undefined,
                        isCriteria: undefined,
                        criteria: undefined,
                        _fields: undefined,
                      },
                    });
                  },
                  renderValue: (val: any) => {
                    if (val) {
                      return 'This Record';
                    } else {
                      return 'Related Record';
                    }
                  },
                }}
              >
                {[
                  {
                    value: true,
                    label: 'This record and lookups from this record',
                    description: 'This Record',
                  },
                  {
                    value: false,
                    label: 'Lookups to this record',
                    description: 'Related Record',
                  },
                ].map((type) => (
                  <MenuItem key={type.value as any} value={type.value as any}>
                    {type.label}
                  </MenuItem>
                ))}
              </SweepSelect>
            </Box>
          </>
        )}
        {action?.actionType === AutomationActionType.VerifyURL && (
          <Box
            sx={{
              border: `1px solid ${colors.grey[300]}`,
              borderRadius: '4px',
              background: colors.white,
              maxWidth: '500px',
            }}
          >
            <NestedFieldsSelector
              placeholder="Select field"
              filterBy={(field: SweepField) => field?.sfDisplayType === SfdcDisplayType.URL}
              crmOrgId={crmOrgId}
              objectType={objectName || ''}
              nestedPath={getEnrichedNestedPath(
                (_action as VerifyUrlAutomationAction).actionParams?.urlFieldId,
              )}
              readonly={readonly}
              onChange={(_sweepField) => {
                onChange({
                  ...action,
                  actionParams: {
                    ...(action as VerifyUrlAutomationAction).actionParams,
                    urlFieldId: _sweepField.fieldIds,
                  },
                });
              }}
            />
          </Box>
        )}
        {action?.actionType === AutomationActionType.UpdateRecords &&
          (_action as UpdateOrCreateAutomationAction).actionParams?.thisRecord === false && (
            <>
              <Box>
                <FormControl sx={{ minWidth: '120px', marginRight: '8px' }}>
                  <ChildRelationshipSelector
                    readonly={readonly}
                    ChildRelationshipObject={
                      (_action as UpdateOrCreateAutomationAction)?.actionParams
                        ?.childRelationshipFieldName
                    }
                    crmOrgId={crmOrgId}
                    objectName={objectName}
                    onChange={(value) => {
                      onChange({
                        ...action,
                        actionParams: {
                          ...action.actionParams,
                          childRelationshipFieldName: value,
                          criteria: undefined,
                          _fields: undefined,
                        },
                      });
                    }}
                  />
                </FormControl>
              </Box>
            </>
          )}
        {action?.actionType === AutomationActionType.UpdateRecords &&
          (_action as UpdateOrCreateAutomationAction).actionParams?.thisRecord === false &&
          (_action as UpdateOrCreateAutomationAction).actionParams?.childRelationshipFieldName && (
            <>
              <Box>
                <SweepSelect
                  FormControlProps={{
                    sx: { minWidth: '140px', marginRight: '8px' },
                  }}
                  SelectProps={{
                    disabled: readonly,
                    placeholder: 'Choose Criteria',
                    value:
                      (_action as UpdateOrCreateAutomationAction).actionParams?.isCriteria ?? '',
                    onChange: (event: SelectChangeEvent<unknown>) => {
                      const val = event.target.value;
                      onChange({
                        ...(_action as UpdateOrCreateAutomationAction),
                        actionParams: {
                          ...(_action as UpdateOrCreateAutomationAction).actionParams,
                          isCriteria: val as boolean,
                        },
                      });
                    },
                    renderValue: (val: any) => {
                      if (val) {
                        return 'Only Records That Meet These Criteria';
                      } else {
                        return `No Criteria - update all related ${
                          (_action as UpdateOrCreateAutomationAction).actionParams
                            ?.childRelationshipFieldName?.objectTypeLabel
                        }`;
                      }
                    },
                  }}
                >
                  {[
                    {
                      value: false,
                      label: `No Criteria - update all related ${
                        (_action as UpdateOrCreateAutomationAction).actionParams
                          ?.childRelationshipFieldName?.objectTypeLabel
                      }`,
                    },
                    {
                      value: true,
                      label: 'Only Records That Meet These Criteria',
                    },
                  ].map((type) => (
                    <MenuItem key={type.value as any} value={type.value as any}>
                      {type.label}
                    </MenuItem>
                  ))}
                </SweepSelect>
              </Box>
            </>
          )}
        {'Lead' === objectName && action?.actionType === AutomationActionType.ConvertLead && (
          <ConvertLeadConvertedStatus
            readonly={readonly}
            crmOrgId={crmOrgId}
            onChange={(val) => {
              onChange({
                ...(_action as ConvertLeadAutomationAction),
                actionParams: {
                  ...(_action as ConvertLeadAutomationAction).actionParams,
                  convertedStatus: val,
                },
              });
            }}
            value={(_action as ConvertLeadAutomationAction).actionParams?.convertedStatus ?? ''}
          />
        )}

        {(action?.actionType === AutomationActionType.Slack ||
          action?.actionType === AutomationActionType.SendTeamsMessage) && (
          <Box sx={{ ml: '12px' }}>
            <Button
              size="medium"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              startIconName={isSampleOpen ? 'Hide' : 'Show'}
              variant="flat-blue"
            >
              {isSampleOpen ? 'Hide example' : 'Show example'}
            </Button>
            <Popover
              open={isSampleOpen}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              <Box sx={{ p: '4px', pb: 0 }}>
                <img
                  src={
                    action?.actionType === AutomationActionType.SendTeamsMessage
                      ? teamsSampleImg
                      : slackSampleImg
                  }
                  width={'665px'}
                />
              </Box>
            </Popover>
          </Box>
        )}
      </Box>
      {(action?.actionType === AutomationActionType.Slack ||
        action?.actionType === AutomationActionType.SendReportToSlack) &&
        slackStatus !== SlackConnectionStatus.Connected && (
          <Box sx={{ mt: '16px' }}>
            <SlackNotConnected slackStatus={slackStatus} />
          </Box>
        )}
      {action?.actionType === AutomationActionType.SendEmail && (
        <AutomationNotifyEmail
          action={_action as EmailAutomationAction}
          crmOrgId={crmOrgId}
          onChange={onChange}
          objectName={objectName}
          readonly={readonly}
        />
      )}

      {'Lead' === objectName && action?.actionType === AutomationActionType.ConvertLead && (
        <ConvertLeadActionNew
          excludeValueType={excludeValueType}
          readonly={readonly}
          action={_action as ConvertLeadAutomationAction}
          crmOrgId={crmOrgId}
          onChange={onChange}
          initialObjectName={objectName}
          isNestedVerifiedUrl={isNestedVerifiedUrl}
        />
      )}

      {action?.actionType === AutomationActionType.Slack && (
        <SlackNotificationAction
          readonly={readonly}
          action={_action as SlackAutomationAction}
          crmOrgId={crmOrgId}
          onChange={onChange}
          objectName={objectName}
        />
      )}

      {action?.actionType === AutomationActionType.SlackDealRoom &&
        (dealRoomType === DealroomType.SLACK || !msTeamsCreateAndAddAction) && (
          <SlackDealRoomAction
            readonly={readonly}
            action={_action as SlackDealRoomAutomationAction}
            crmOrgId={crmOrgId}
            onChange={onChange}
            objectName={objectName}
          />
        )}

      {action?.actionType === AutomationActionType.SlackAddMember &&
        (dealRoomType === DealroomType.SLACK || !msTeamsCreateAndAddAction) && (
          <SlackAddMemberAction
            readonly={readonly}
            action={_action as SlackAddMemberAutomationAction}
            crmOrgId={crmOrgId}
            onChange={onChange}
            objectName={objectName}
          />
        )}

      {action?.actionType === AutomationActionType.SlackRenameDealroom &&
        (dealRoomType === DealroomType.SLACK || !msTeamsCreateAndAddAction) && (
          <SlackRenameDealroomAction
            readonly={readonly}
            action={_action as SlackRenameDealroomAutomationAction}
            crmOrgId={crmOrgId}
            onChange={onChange}
            objectName={objectName}
          />
        )}

      {action?.actionType === AutomationActionType.TeamsRenameDealroom &&
        dealRoomType === DealroomType.TEAMS && (
          <TeamsRenameDealRoomAction
            readonly={readonly}
            action={_action as TeamsRenameDealroomAutomationAction}
            crmOrgId={crmOrgId}
            onChange={onChange}
            objectName={objectName}
          />
        )}

      {action?.actionType === AutomationActionType.SlackArchiveDealroom &&
        (dealRoomType === DealroomType.SLACK || !msTeamsCreateAndAddAction) && (
          <SlackArchiveDealroomAction
            readonly={readonly}
            action={_action as SlackArchiveDealroomAutomationAction}
            crmOrgId={crmOrgId}
            onChange={onChange}
            objectName={objectName}
          />
        )}

      {action?.actionType === AutomationActionType.TeamsArchiveDealroom &&
        dealRoomType === DealroomType.TEAMS && (
          <TeamsArchiveDealroomAction
            readonly={readonly}
            action={_action as TeamsArchiveDealroomAutomationAction}
            crmOrgId={crmOrgId}
            onChange={onChange}
            objectName={objectName}
          />
        )}

      {action?.actionType === AutomationActionType.SendTeamsMessage && (
        <TeamsMessageAction
          readonly={readonly}
          action={_action as SendTeamsMessageAutomationAction}
          crmOrgId={crmOrgId}
          onChange={onChange}
          objectName={objectName}
        />
      )}

      {action?.actionType === AutomationActionType.TeamsAddMember &&
        dealRoomType === DealroomType.TEAMS && (
          <TeamsAddMemberAction
            readonly={readonly}
            action={_action as TeamsAddMemberAutomationAction}
            crmOrgId={crmOrgId}
            onChange={onChange}
            objectName={objectName}
          />
        )}

      {action?.actionType === AutomationActionType.TeamsDealRoom &&
        dealRoomType === DealroomType.TEAMS && (
          <TeamsDealRoomAction
            readonly={readonly}
            action={_action as TeamsDealRoomAutomationAction}
            crmOrgId={crmOrgId}
            onChange={onChange}
            objectName={objectName}
          />
        )}

      {action?.actionType === AutomationActionType.PushNotification && (
        <PushNotificationAction
          readonly={readonly}
          action={_action as PushNotificationAutomationAction}
          crmOrgId={crmOrgId}
          onChange={onChange}
          objectName={objectName}
        />
      )}

      {(action?.actionType === AutomationActionType.CreateRecord || // TODO: REMOVE 'CloneRecords' with ft lgDemo
        action?.actionType === AutomationActionType.CloneRecords) &&
        objectName === CONTACT &&
        action.actionParams?.objectTypeName?.label === OPPORTUNITY && (
          <ContactRoleSelector
            isShowPrimary={true}
            lookupFieldsBy={lookupFieldsTypes.createContactOpp}
            showTooltip
            objectToSearch={OPPORTUNITY_CONTACT_ROLE}
            fieldToSearch={'Role'}
            actionOcrObject={action.actionParams?.relationsRecords?.find(
              (el) => el.objectType === OPPORTUNITY_CONTACT_ROLE,
            )}
            crmOrgId={crmOrgId}
            onChangeCheckbox={(isChecked, ocrElement) => {
              onChange({
                ...action,
                actionParams: {
                  ...action.actionParams,
                  relationsRecords: isChecked ? [ocrElement] : undefined,
                },
              });
            }}
            onSelectRole={(_relationsObject) => {
              onChange({
                ...action,
                actionParams: {
                  ...action.actionParams,
                  relationsRecords: [_relationsObject],
                },
              });
            }}
          />
        )}

      {(action?.actionType === AutomationActionType.CreateRecord || // TODO: REMOVE 'CloneRecords' with ft lgDemo
        action?.actionType === AutomationActionType.CloneRecords) &&
        objectName === OPPORTUNITY &&
        action.actionParams?.objectTypeName?.label === OPPORTUNITY && (
          <ContactRoleSelector
            readonly={readonly}
            lookupFieldsBy={lookupFieldsTypes.CloneLineItem}
            objectToSearch={OPPORTUNITY_LINE_ITEM}
            label={'Copy products from original record'}
            actionOcrObject={action.actionParams?.relationsRecordsToClone?.find(
              (el) => el.objectType === OPPORTUNITY_LINE_ITEM,
            )}
            crmOrgId={crmOrgId}
            onChangeCheckbox={(isChecked, ocrElement) => {
              const relationsRecords = action.actionParams?.relationsRecordsToClone || [];
              onChange({
                ...action,
                actionParams: {
                  ...action.actionParams,
                  relationsRecordsToClone: isChecked
                    ? [...relationsRecords, ocrElement]
                    : relationsRecords.filter((el) => el.objectType !== OPPORTUNITY_LINE_ITEM),
                },
              });
            }}
            hideInnerSelection={true}
          />
        )}

      {(action?.actionType === AutomationActionType.CreateRecord || // TODO: REMOVE 'CloneRecords' with ft lgDemo
        action?.actionType === AutomationActionType.CloneRecords) &&
        objectName === OPPORTUNITY &&
        action.actionParams?.objectTypeName?.label === OPPORTUNITY && (
          <ContactRoleSelector
            readonly={readonly}
            lookupFieldsBy={lookupFieldsTypes.cloneContactRole}
            objectToSearch={OPPORTUNITY_CONTACT_ROLE}
            label={'Copy contact roles from original record'}
            actionOcrObject={action.actionParams?.relationsRecordsToClone?.find(
              (el) => el.objectType === OPPORTUNITY_CONTACT_ROLE,
            )}
            crmOrgId={crmOrgId}
            onChangeCheckbox={(isChecked, ocrElement) => {
              const relationsRecords = action.actionParams?.relationsRecordsToClone || [];
              onChange({
                ...action,
                actionParams: {
                  ...action.actionParams,
                  relationsRecordsToClone: isChecked
                    ? [...relationsRecords, ocrElement]
                    : relationsRecords.filter((el) => el.objectType !== OPPORTUNITY_CONTACT_ROLE),
                },
              });
            }}
            hideInnerSelection={true}
          />
        )}

      {action?.actionType === AutomationActionType.AssignmentRules && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Typography variant="body">Run the following assignment rules:</Typography>
          <AssignmentRulesEditor
            assignmentRules={
              (action.actionParams?.assignmentRules ?? []) as AutomationAssignmentRule[]
            }
            onChange={(assignmentRules: AutomationAssignmentRule[]) =>
              onChange({
                ...action,
                actionParams: { assignmentRules },
              })
            }
            crmOrgId={crmOrgId}
            objectName={objectName}
            readonly={readonly}
          />
        </Box>
      )}

      {action?.actionType === AutomationActionType.PrioritizedActions && (
        <Box display="flex" flexDirection="column" gap={1} mt={1}>
          <Typography variant="body">
            Evaluate the following branches, and execute the first one that meet the criteria:
          </Typography>
          <ConditionalActionsEditor
            objectTypes={objectTypes}
            conditionalActions={
              (action.actionParams?.prioritizedActions ?? []) as AutomationConditionalAction[]
            }
            onChange={(prioritizedActions) =>
              onChange({
                ...action,
                actionParams: { prioritizedActions },
              })
            }
            crmOrgId={crmOrgId}
            objectName={objectName}
            readonly={readonly}
          />
        </Box>
      )}

      {action?.actionType === AutomationActionType.SendReportToSlack && (
        <SendReportToSlackAction
          readonly={readonly}
          action={_action as SendReportToSlackAction}
          crmOrgId={crmOrgId}
          onChange={onChange}
          objectName={objectName}
        />
      )}

      {action?.actionType === AutomationActionType.VerifyURL && (
        <VerifyUrlActionWrapper
          readonly={readonly}
          action={_action as VerifyUrlAutomationAction}
          crmOrgId={crmOrgId}
          onChange={onChange}
          objectName={objectName}
          objectTypes={objectTypes}
        />
      )}

      {action?.actionType === AutomationActionType.Webhook &&
        action?.actionParams?.integrationId && (
          <WebhookActionWrapper
            readonly={readonly}
            action={_action as WebhookAutomationAction}
            crmOrgId={crmOrgId}
            onChange={onChange}
            objectName={objectName}
            objectTypes={objectTypes}
          />
        )}

      {action?.actionType === AutomationActionType.RunApex && (
        <RunApexActionWrapper
          action={_action as RunApexAutomationAction}
          crmOrgId={crmOrgId}
          onChange={onChange}
          readonly={readonly}
        />
      )}

      {((action?.actionType === AutomationActionType.UpdateRecords &&
        (_action as UpdateOrCreateAutomationAction).actionParams?.thisRecord) ||
        (action?.actionType === AutomationActionType.UpdateRecords &&
          (_action as UpdateOrCreateAutomationAction).actionParams?.thisRecord === false &&
          (_action as UpdateOrCreateAutomationAction).actionParams.isCriteria !== undefined) ||
        ((action?.actionType === AutomationActionType.CreateRecord || // TODO: REMOVE 'CloneRecords' with ft lgDemo
          action?.actionType === AutomationActionType.CloneRecords) &&
          (_action as UpdateOrCreateAutomationAction).actionParams?.objectTypeName)) && (
        <Box sx={{ display: 'flex', marginTop: '8px', flexDirection: 'column' }}>
          {action?.actionType === AutomationActionType.UpdateRecords &&
            (_action as UpdateOrCreateAutomationAction).actionParams.isCriteria === true && (
              <Box
                sx={{
                  backgroundColor: colors.grey[100],
                  borderRadius: '6px',
                  marginBottom: '8px',
                  pt: '12px',
                }}
              >
                <>
                  <CustomHeader customHeader={'When these criteria are met:'} />
                  <SweepFieldsRuleBuilder
                    filterOperatorBy={getOperatorsWithoutRecordsComparison}
                    ref={ruleBuilderRef}
                    readOnly={readonly}
                    crmOrgId={crmOrgId ?? ''}
                    objectType={
                      (_action as UpdateOrCreateAutomationAction).actionParams
                        .childRelationshipFieldName?.objectType || ''
                    }
                    referenceObjectType={objectName}
                    sweepCondition={sweepCondition}
                    onChange={(newSweepCondition) => {
                      const exitCriteria = criteria || {
                        criteria: [],
                        criteriaLogic: '',
                      };
                      onChange({
                        ...(_action as UpdateOrCreateAutomationAction),
                        actionParams: {
                          ...(_action as UpdateOrCreateAutomationAction).actionParams,
                          criteria: {
                            ...exitCriteria,
                            ...newSweepCondition,
                          },
                        },
                      });
                    }}
                    disableResolvePolymorphic
                    displayValueContextMenu={true}
                  />
                </>
              </Box>
            )}
          <Box sx={{ display: 'flex' }}>
            <Box
              sx={{
                marginRight: '12px',
                paddingTop: '10px',
                flex: '0 0 auto',
                maxWidth: '160px',
                width: '160px',
                overflow: 'auto',
              }}
            >
              <Typography variant="body">
                {action?.actionType === AutomationActionType.CreateRecord || // TODO: REMOVE 'CloneRecords' with ft lgDemo
                action?.actionType === AutomationActionType.CloneRecords ? (
                  <>
                    Set the new{' '}
                    <b>
                      {
                        (
                          _action as UpdateOrCreateAutomationAction as UpdateOrCreateAutomationAction
                        ).actionParams?.objectTypeName?.label
                      }
                    </b>
                    ’s field value(s):
                  </>
                ) : action?.actionParams?.thisRecord ? (
                  <>
                    Update these <b> {objectName} </b> field(s):
                  </>
                ) : (
                  <>
                    Update these
                    <b> {action.actionParams?.childRelationshipFieldName?.objectTypeLabel} </b>
                    field(s):
                  </>
                )}
              </Typography>
            </Box>
            <Box className="automationFieldLineWrapper" sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <AutomationActionRows
                  showVerifiedUrlOption={isNestedVerifiedUrl}
                  excludeValueType={excludeValueType}
                  showCreatableFields={
                    action?.actionType === AutomationActionType.CreateRecord || // TODO: REMOVE 'CloneRecords' with ft lgDemo
                    action?.actionType === AutomationActionType.CloneRecords
                  }
                  disableLookupItems={
                    action?.actionType === AutomationActionType.CreateRecord || // TODO: REMOVE 'CloneRecords' with ft lgDemo
                    action?.actionType === AutomationActionType.CloneRecords
                  }
                  hideRecordTypeIdField={action?.actionType === 'CREATE_RECORD'}
                  readonly={readonly}
                  objectName={getActionRowObjectType()}
                  cmOrgId={crmOrgId}
                  initialObjectName={objectName}
                  onChange={_onChangeActionRowFields}
                  fields={action.actionParams?._fields || []}
                  isShowWebhookOption={isShowWebhookOption}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};
